
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Toggle } from '@/components/ui/toggle';
import { Plus } from 'lucide-react';
import { DateRangePicker } from '@/components/financial-reports/date-range-picker';
import type { DateRange } from 'react-day-picker';
import { PaymentTable } from '@/components/payments/payment-table';
import { AddPaymentDialog } from '@/components/payments/add-payment-dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useGym } from '@/contexts/gym-context';
import { useUser } from '@/contexts/user-context';
import { useView } from '@/contexts/view-context';

export default function PaymentsPage() {
  const { t } = useTranslation();
  const [addPaymentOpen, setAddPaymentOpen] = useState(false);
  const { user, isLoading: isUserLoading } = useUser();
  const { viewMode } = useView();

  const { gyms, selectedGym, selectGym, isLoading: isGymsLoading } = useGym();

  useEffect(() => {
    if (viewMode === 'reception') {
      selectGym(selectedGym);
    }
  }, []);

  // Filter gyms based on user's assigned gyms
  const userGyms =
    user && user.gym ? gyms.filter((gym) => user.gym.includes(gym._id)) : gyms;

  // Set default date range to today only
  const today = new Date();

  const [dateRange, setDateRange] = useState<DateRange>({
    from: today,
    to: today,
  });

  // Comparison state
  const [comparisonMode, setComparisonMode] = useState(false);
  const [comparisonDateRange, setComparisonDateRange] = useState<DateRange>({
    from: undefined,
    to: undefined,
  });

  // Format dates to DD-MM-YYYY
  const formatDateForAPI = (date: Date) => {
    return format(date, 'dd-MM-yyyy');
  };

  const startDate = dateRange?.from ? formatDateForAPI(dateRange.from) : '';
  const endDate = dateRange?.to ? formatDateForAPI(dateRange.to) : '';

  const comparisonStartDate = comparisonDateRange?.from
    ? formatDateForAPI(comparisonDateRange.from)
    : '';
  const comparisonEndDate = comparisonDateRange?.to
    ? formatDateForAPI(comparisonDateRange.to)
    : '';

  const handleComparisonModeChange = (enabled: boolean) => {
    setComparisonMode(enabled);
    if (!enabled) {
      setComparisonDateRange({ from: undefined, to: undefined });
    }
  };

  // Wrapper functions for DateRangePicker compatibility
  const handleDateRangeChange = (range: DateRange | undefined) => {
    setDateRange(range || { from: undefined, to: undefined });
  };

  const handleComparisonDateRangeChange = (range: DateRange | undefined) => {
    setComparisonDateRange(range || { from: undefined, to: undefined });
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div className="flex items-center gap-4">
          <h1 className="text-3xl font-bold">{t('payments.title')}</h1>
          {(viewMode === 'admin' || viewMode === 'reception') &&
            userGyms.length > 1 && (
              <Select value={selectedGym} onValueChange={selectGym}>
                <SelectTrigger className="w-[180px] md:w-[220px]">
                  <SelectValue placeholder={t('dashboard.selectGym')}>
                    {userGyms.find((gym) => gym._id === selectedGym)?.name ||
                      t('dashboard.selectGym')}
                  </SelectValue>
                </SelectTrigger>
                <SelectContent>
                  {/* {viewMode === "admin" && userGyms.length > 1 && (
                  <SelectItem value="all">{t("dashboard.allGyms")}</SelectItem>
                )} */}
                  {userGyms.map((gym) => (
                    <SelectItem key={gym._id} value={gym._id}>
                      {gym.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
        </div>

        <Button onClick={() => setAddPaymentOpen(true)}>
          <Plus className="h-4 w-4 mr-2" />
          {t('payments.addPayment')}
        </Button>
      </div>

      <div className="flex items-center gap-4 mb-6">
        <div>
          <label className="text-sm font-medium mb-2 block">
            {t('payments.selectedPeriod', 'Selected Period')}
          </label>
          <DateRangePicker
            dateRange={dateRange}
            setDateRange={handleDateRangeChange}
            placeholder={t('financial.selectDateRange')}
          />
        </div>

        <div className="flex items-center gap-2" style={{ marginTop: '25px' }}>
          <Toggle
            pressed={comparisonMode}
            onPressedChange={handleComparisonModeChange}
            aria-label="Toggle comparison mode"
          >
            {t('payments.enableComparison', 'Enable Comparison')}
          </Toggle>
        </div>

        {comparisonMode && (
          <div>
            <label className="text-sm font-medium mb-2 block">
              {t('payments.comparisonPeriod', 'Comparison Period')}
            </label>
            <DateRangePicker
              dateRange={comparisonDateRange}
              setDateRange={handleComparisonDateRangeChange}
              placeholder={t('payments.selectComparisonDateRange')}
            />
          </div>
        )}
      </div>

      <Card>
        <CardHeader>
          <CardTitle>{t('payments.paymentsList')}</CardTitle>
        </CardHeader>
        <CardContent>
          {startDate && endDate && (
            <PaymentTable
              startDate={startDate}
              endDate={endDate}
              comparisonStartDate={comparisonStartDate}
              comparisonEndDate={comparisonEndDate}
              comparisonMode={comparisonMode}
            />
          )}
        </CardContent>
      </Card>

      <AddPaymentDialog
        open={addPaymentOpen}
        onOpenChange={setAddPaymentOpen}
      />
    </div>
  );
}
